import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  getBranches, getDepartments, addUpdateBranches, addUpdateDepartments
} from "../../../api/General/Company";
import { BranchInput, DepartmentInput, Branch, Department } from "../../../models/general";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from "../../../components/ui/Dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "../../../components/ui/Table";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Spinner } from "../../../components/ui/Spinner";
import { PageLayout } from "../../../components/ui/PageLayout";
import { GitBranch } from "lucide-react";

export default function BranchesAndDepartments() {
  const { t } = useTranslation();
  const companyId = useSelector((s: any) => s.user.company.id);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [openAddBranch, setOpenAddBranch] = useState(false);
  const [openAddDepartment, setOpenAddDepartment] = useState(false);
  const [branchInput, setBranchInput] = useState<BranchInput>({} as BranchInput);
  const [departmentInput, setDepartmentInput] = useState<DepartmentInput>({} as DepartmentInput);
  const [editBranchInput, setEditBranchInput] = useState<BranchInput>({} as BranchInput);
  const [editDepartmentInput, setEditDepartmentInput] = useState<DepartmentInput>({} as DepartmentInput);
  const [editBranchId, setEditBranchId] = useState<number | null>(null);
  const [editDepartmentId, setEditDepartmentId] = useState<number | null>(null);

  useEffect(() => {
    fetchBranchesAndDepartments();
  }, [companyId]);

  const fetchBranchesAndDepartments = async () => {
    setLoading(true);
    try {
      const branchesResponse = await getBranches(companyId);
      const departmentsResponse = await getDepartments(companyId);
      setBranches(branchesResponse.payload);
      setDepartments(departmentsResponse.payload);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBranch = async () => {
    setLoading(true);
    try {
      await addUpdateBranches([branchInput]);
      setOpenAddBranch(false);
      fetchBranchesAndDepartments();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async () => {
    setLoading(true);
    try {
      await addUpdateDepartments([departmentInput]);
      setOpenAddDepartment(false);
      fetchBranchesAndDepartments();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBranch = async () => {
    if (editBranchId !== null) {
      setLoading(true);
      try {
        await addUpdateBranches([editBranchInput]);
        setEditBranchId(null);
        fetchBranchesAndDepartments();
      } catch (ex) {
        console.log(ex);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateDepartment = async () => {
    if (editDepartmentId !== null) {
      setLoading(true);
      try {
        await addUpdateDepartments([editDepartmentInput]);
        setEditDepartmentId(null);
        fetchBranchesAndDepartments();
      } catch (ex) {
        console.log(ex);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditBranch = (branch: Branch) => {
    setEditBranchInput(branch);
    setEditBranchId(branch.id);
  };

  const handleEditDepartment = (department: Department) => {
    setEditDepartmentInput({ id: department.id, name: department.name, parentDepartmentId: department.parentDepartmentId ?? null });
    setEditDepartmentId(department.id);
  };

  return (
    <PageLayout
      title={t("Branches & Departments")}
      icon={GitBranch}
      accentColor="slate"
      headerActions={(
        <div className="flex gap-2">
          <Button variant="outline" iconName="Plus" onClick={() => setOpenAddBranch(true)}>
            {t("Add Branch")}
          </Button>
          <Button variant="outline" iconName="Plus" onClick={() => setOpenAddDepartment(true)}>
            {t("Add Department")}
          </Button>
        </div>
      )}
    >
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Branch Name")}</TableHead>
              <TableHead>{t("Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {branches.map((branch) => (
              <TableRow key={branch.id}>
                <TableCell>{branch.name}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => handleEditBranch(branch)}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-lg border border-border bg-card mt-5">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Department Name")}</TableHead>
              <TableHead>{t("Parent Department")}</TableHead>
              <TableHead>{t("Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {departments.map((department) => (
              <TableRow key={department.id}>
                <TableCell>{department.name}</TableCell>
                <TableCell>{department.parentDepartmentName ?? t("None (Top Level)")}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => handleEditDepartment(department)}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add Branch Dialog */}
      <Dialog open={openAddBranch} onOpenChange={setOpenAddBranch}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("Add Branch")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              autoFocus
              label={t("Branch Name")}
              value={branchInput.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBranchInput({ ...branchInput, name: e.target.value })}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAddBranch(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleAddBranch} disabled={loading}>
              {loading ? <Spinner size="sm" /> : t("Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Department Dialog */}
      <Dialog open={openAddDepartment} onOpenChange={setOpenAddDepartment}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("Add Department")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-3">
              <Input
                autoFocus
                label={t("Department Name")}
                value={departmentInput.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDepartmentInput({ ...departmentInput, name: e.target.value })}
              />
              <Select
                label={t("Parent Department")}
                placeholder={t("None (Top Level)")}
                clearable
                searchable
                options={departments.map(d => ({ value: d.id, label: d.name }))}
                value={departmentInput.parentDepartmentId ?? undefined}
                onChange={(val: number | undefined) => setDepartmentInput({ ...departmentInput, parentDepartmentId: val ?? null })}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAddDepartment(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleAddDepartment} disabled={loading}>
              {loading ? <Spinner size="sm" /> : t("Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Branch Dialog */}
      <Dialog open={editBranchId !== null} onOpenChange={(open) => { if (!open) setEditBranchId(null); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("Edit Branch")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              autoFocus
              label={t("Branch Name")}
              value={editBranchInput.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditBranchInput({ ...editBranchInput, name: e.target.value })}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBranchId(null)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleUpdateBranch} disabled={loading}>
              {loading ? <Spinner size="sm" /> : t("Update")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Department Dialog */}
      <Dialog open={editDepartmentId !== null} onOpenChange={(open) => { if (!open) setEditDepartmentId(null); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("Edit Department")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-3">
              <Input
                autoFocus
                label={t("Department Name")}
                value={editDepartmentInput.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDepartmentInput({ ...editDepartmentInput, name: e.target.value })}
              />
              <Select
                label={t("Parent Department")}
                placeholder={t("None (Top Level)")}
                clearable
                searchable
                options={departments
                  .filter(d => d.id !== editDepartmentId)
                  .map(d => ({ value: d.id, label: d.name }))}
                value={editDepartmentInput.parentDepartmentId ?? undefined}
                onChange={(val: number | undefined) => setEditDepartmentInput({ ...editDepartmentInput, parentDepartmentId: val ?? null })}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDepartmentId(null)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleUpdateDepartment} disabled={loading}>
              {loading ? <Spinner size="sm" /> : t("Update")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
